import { useIntl } from "react-intl";
import { Button } from "@/components/design/Button";
import { Select } from "@/components/design/Select";
import type { FieldOption } from "@/lib/field-util";

interface TeamProfileCreateFormProps {
  german: string;
  english: string;
  onGermanChange: (value: string) => void;
  onEnglishChange: (value: string) => void;
  /** Profiles whose weights the new one can start from; `value` is the profile id. */
  sources: readonly FieldOption[];
  /** null = neutral baseline, otherwise the id of one of `sources`. */
  sourceId: string | null;
  onSourceChange: (id: string | null) => void;
  /** Whether the source picker is shown; until then a chip stands in for it. */
  sourceOpen: boolean;
  onOpenSource: () => void;
  busy: boolean;
  nameTaken: boolean;
  onSubmit: () => void;
}

const INPUT_CLASS =
  "bg-surface w-full rounded-md px-3 py-2 text-smycles-body ring-1 ring-ink/10 focus:outline-none focus:ring-ink-ui disabled:opacity-60";

// Rechte Spalte des Master-Detail-Layouts, wenn kein Profil gewaehlt ist. Der
// Name ist Pflicht, die Gewichtung startet neutral oder uebernimmt die einer
// bestehenden Scorecard; feinjustiert wird danach in der Karte. "Duplizieren"
// in der Karte landet hier mit vorbelegter Quelle.
export function TeamProfileCreateForm({
  german,
  english,
  onGermanChange,
  onEnglishChange,
  sources,
  sourceId,
  onSourceChange,
  sourceOpen,
  onOpenSource,
  busy,
  nameTaken,
  onSubmit,
}: TeamProfileCreateFormProps) {
  const intl = useIntl();
  const source = sources.find((s) => s.value === sourceId) ?? null;
  const canSubmit = !busy && !nameTaken && german.trim().length > 0 && english.trim().length > 0;

  return (
    <div className="box bg-surface p-5">
      <div className="text-smycles-body font-medium">
        {intl.formatMessage({ id: "teamProfile.create.title", defaultMessage: "New profile" })}
      </div>
      <div className="mt-1 text-smycles-meta text-ink-muted">
        {source
          ? intl.formatMessage(
              {
                id: "teamProfile.create.subFrom",
                defaultMessage:
                  "Starts with the weights of “{name}”. Open the profile afterwards to tune the weights.",
              },
              { name: source.label },
            )
          : intl.formatMessage({
              id: "teamProfile.create.sub",
              defaultMessage:
                "Starts with every power weighted equally. Open the profile afterwards to tune the weights.",
            })}
      </div>
      <div className="mt-3 grid grid-cols-1 gap-3 @sm:grid-cols-2">
        <input
          id="teamprofile-createde"
          value={german}
          onChange={(e) => onGermanChange(e.target.value)}
          disabled={busy}
          placeholder={intl.formatMessage({ id: "teamProfile.nameDe", defaultMessage: "Name (German)" })}
          aria-label={intl.formatMessage({ id: "teamProfile.nameDe", defaultMessage: "Name (German)" })}
          className={INPUT_CLASS}
        />
        <input
          id="teamprofile-createen"
          value={english}
          onChange={(e) => onEnglishChange(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter" && canSubmit) onSubmit();
          }}
          disabled={busy}
          placeholder={intl.formatMessage({ id: "teamProfile.nameEn", defaultMessage: "Name (English)" })}
          aria-label={intl.formatMessage({ id: "teamProfile.nameEn", defaultMessage: "Name (English)" })}
          className={INPUT_CLASS}
        />
        {/* Die Quelle ist der Ausnahmefall, deshalb steht bis zum Klick nur
            der Chip (UI_CONVENTIONS: chip = "Picker oeffnen"), nicht das Select. */}
        {sourceOpen ? (
          <label className="block">
            <span className="text-smycles-meta text-ink-muted">
              {intl.formatMessage({
                id: "teamProfile.create.source",
                defaultMessage: "Weights from an existing scorecard",
              })}
            </span>
            <Select
              className="mt-1"
              value={sourceId}
              onChange={(next) => onSourceChange(next === "" ? null : next)}
              options={sources}
              placeholder={intl.formatMessage({
                id: "teamProfile.create.sourceNone",
                defaultMessage: "None (weighted equally)",
              })}
              disabled={busy}
            />
          </label>
        ) : (
          <div className="self-center">
            <Button variant="chip" size="sm" disabled={busy} onClick={onOpenSource}>
              {intl.formatMessage({
                id: "teamProfile.create.source",
                defaultMessage: "Weights from an existing scorecard",
              })}
            </Button>
          </div>
        )}
      </div>
      {nameTaken ? (
        <div id="teamprofile-createduplicate" className="mt-2 text-smycles-meta text-ink-muted">
          {intl.formatMessage({
            id: "teamProfile.duplicate",
            defaultMessage: "A profile with this name already exists.",
          })}
        </div>
      ) : null}
      <Button
        id="teamprofile-create"
        variant="primary"
        size="compact"
        className="mt-3"
        disabled={!canSubmit}
        onClick={onSubmit}
      >
        {intl.formatMessage({ id: "teamProfile.create.submit", defaultMessage: "Create profile" })}
      </Button>
    </div>
  );
}
